// The cookie is a compact string (`matic_consent=v1.a1.m0.e`), not JSON, so
// the inline beforeInteractive guards in _document.tsx can test it with a
// regex before any bundle exists.

import { ACQUISITION_STORAGE_KEY } from "@/utils/acquisition";

export const CONSENT_COOKIE_NAME = "matic_consent";

// Bumping re-prompts everyone: stored choices from older versions stop
// matching and trackers load by default again until the visitor re-chooses.
export const CONSENT_VERSION = 1;

export type ConsentSource =
  | "default" // nothing stored: everything allowed
  | "gpc" // nothing stored, browser sent Global Privacy Control
  | "explicit" // Accept all, or saved from the preferences dialog
  | "acknowledged"; // dismissed the banner, keeping whatever the defaults were

export type ConsentChoice = {
  analytics: boolean;
  marketing: boolean;
};

export type Consent = ConsentChoice & {
  source: ConsentSource;
};

const SOURCE_CODES = { explicit: "e", acknowledged: "k" } as const;

export type StoredConsentSource = keyof typeof SOURCE_CODES;

const DEFAULT_CONSENT: Consent = {
  analytics: true,
  marketing: true,
  source: "default",
};

export function hasGpcSignal(): boolean {
  return (
    typeof navigator !== "undefined" && navigator.globalPrivacyControl === true
  );
}

/**
 * The consent a cookie string carries, falling back to the defaults. Shared by
 * the browser and by API routes, which read the request's Cookie header and
 * the Sec-GPC header.
 */
export function parseConsent(cookies: string, hasGpc: boolean): Consent {
  const match = cookies.match(
    new RegExp(
      `(?:^|; ?)${CONSENT_COOKIE_NAME}=v${CONSENT_VERSION}\\.a(\\d)\\.m(\\d)\\.([ek])`,
    ),
  );
  if (match) {
    return {
      analytics: match[1] === "1",
      marketing: match[2] === "1",
      source: match[3] === "e" ? "explicit" : "acknowledged",
    };
  }
  if (hasGpc) {
    return { analytics: true, marketing: false, source: "gpc" };
  }
  return DEFAULT_CONSENT;
}

export function getConsent(): Consent {
  if (typeof document === "undefined") return DEFAULT_CONSENT;
  return parseConsent(document.cookie, hasGpcSignal());
}

export function hasStoredChoice(): boolean {
  const { source } = getConsent();
  return source === "explicit" || source === "acknowledged";
}

export function storeConsent(
  choice: ConsentChoice,
  source: StoredConsentSource,
): Consent {
  const value = [
    `v${CONSENT_VERSION}`,
    `a${choice.analytics ? 1 : 0}`,
    `m${choice.marketing ? 1 : 0}`,
    SOURCE_CODES[source],
  ].join(".");
  const oneYearSeconds = 60 * 60 * 24 * 365;
  document.cookie = `${CONSENT_COOKIE_NAME}=${value}; Max-Age=${oneYearSeconds}; Path=/; SameSite=Lax; Secure${sharedDomainAttribute()}`;
  return { ...choice, source };
}

export function sharedDomainAttribute(): string {
  return location.hostname.endsWith("maticrobots.com")
    ? "; Domain=.maticrobots.com"
    : "";
}

// Tries every domain scoping an SDK may have used (a delete only matches a
// cookie with the same domain attribute); PostHog scopes to the eTLD+1, which
// on public-suffix hosts like amplifyapp.com is a parent of the full hostname.
function deleteCookie(name: string): void {
  const parts = location.hostname.split(".");
  const attrs = [""];
  for (let i = 0; i < parts.length - 1; i++) {
    attrs.push(`; Domain=.${parts.slice(i).join(".")}`);
  }
  for (const attr of attrs) {
    document.cookie = `${name}=; Max-Age=0; Path=/${attr}`;
  }
}

export function cleanupDeclinedStorage(choice: ConsentChoice): void {
  try {
    if (!choice.marketing) {
      deleteCookie("impact_profile_id");
      deleteCookie("_fbp");
      deleteCookie("_fbc");
      deleteCookie("_pin_unauth");
      deleteCookie("_epik");
      deleteCookie("_derived_epik");
      deleteCookie("__kla_id");
      // The pixel can't be unloaded mid-session; this stops it sending and
      // is remembered, so a later visit stays quiet until a fresh grant.
      window.oaiq?.("consent", false);
      deleteCookie("__oppref");
      deleteCookie("__obref");
      sessionStorage.removeItem(ACQUISITION_STORAGE_KEY);
    }
    if (!choice.analytics) {
      for (const entry of document.cookie.split("; ")) {
        const name = entry.split("=")[0];
        if (name?.startsWith("ph_")) deleteCookie(name);
      }
    }
    // One pass over localStorage for every declined category. Both are prefix
    // matches, not fixed names: `alia*`/`__alia*` are keys the removed Alia
    // popups left behind (suffixed per shop, user, and campaign), cleared for
    // returning visitors; PostHog writes `ph_*`.
    const declinedPrefixes = [
      ...(choice.marketing ? [] : ["alia", "__alia"]),
      ...(choice.analytics ? [] : ["ph_"]),
    ];
    if (declinedPrefixes.length > 0) {
      for (const key of Object.keys(localStorage)) {
        const name = key.toLowerCase();
        if (declinedPrefixes.some((prefix) => name.startsWith(prefix))) {
          localStorage.removeItem(key);
        }
      }
    }
  } catch (error) {
    console.error("Failed to clean up declined tracking storage:", error);
  }
}
